import { DatastoresTracking } from "@/lib/tracking/datastores";
import { useEffect, useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Tooltip } from "@altimateai/lego";
import {
  Button,
  Card,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Textarea,
} from "@altimateai/lego";
import { DatastoreTypeEnum, datastoreTypeEnum } from "../../types";
import { DatastoreTypeSelectionProps } from "./types";
import { DATASTORE_CONFIG, EnabledDatastoreType } from "./constants";
import { useAppState } from "@/modules/app/useAppContext";
import { Stack } from "@altimateai/lego";
import { IconChevronRight, IconCircleCheckFilled } from "@tabler/icons-react";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { showAzureDatabricksConnections } from "@/helpers/tenant";

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string(),
  type: z.enum(datastoreTypeEnum).nullable(),
});

type FormValues = z.infer<typeof formSchema>;

const DatastoreTypeSelection = ({
  typeData,
  handleSubmit: onSubmit,
  setFormProgressLabels,
}: DatastoreTypeSelectionProps): JSX.Element => {
  const [selectedButton, setSelectedButton] = useState(-1);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      type: null,
    },
  });

  useEffect(() => {
    form.setValue("name", typeData.name ?? "");
    form.setValue("description", typeData.description ?? "");
    form.setValue("type", typeData.type ?? null);
    if (typeData.type && typeData.type in DATASTORE_CONFIG) {
      const typeIndex = Object.values(DATASTORE_CONFIG).findIndex(
        config => config.value === typeData.type
      );
      setSelectedButton(typeIndex);
      setFormProgressLabels(
        DATASTORE_CONFIG[typeData.type as keyof typeof DATASTORE_CONFIG].formSteps
      );
    } else {
      setSelectedButton(-1);
      setFormProgressLabels(["Type", "Connection"]);
    }
  }, [typeData, form, setFormProgressLabels]);

  const handleDatastoreTypeChange = (
    e: React.MouseEvent<HTMLButtonElement>,
    i: number,
    type: EnabledDatastoreType
  ) => {
    e.preventDefault();
    if (i !== selectedButton) {
      DatastoresTracking.typeSelected(type);
      setSelectedButton(i);
      form.setValue("type", type);
      setFormProgressLabels(DATASTORE_CONFIG[type].formSteps);
    } else {
      setSelectedButton(-1);
      form.setValue("type", null);
    }
  };

  const isDatastoreTypeConnected = (): boolean => {
    return false;
    // Currently, we are enabling all datastores for multi instance support
    // if (!datastores?.data_stores) return false;
    // return datastores.data_stores.some(ds => ds.type === type);
  };

  const { enableBiFeature, enableCloudIntegration } = useAppState();

  const azureDatabricksEnabled = showAzureDatabricksConnections();

  const datastoreTypes = Object.values(DATASTORE_CONFIG)
    .filter(config => {
      if (config.value === DatastoreTypeEnum.TABLEAU) {
        return enableBiFeature;
      }
      if (
        config.value === DatastoreTypeEnum.DBT_CLOUD ||
        config.value === DatastoreTypeEnum.DBT_CORE
      ) {
        return enableCloudIntegration;
      }
      if (
        config.value === DatastoreTypeEnum.AZURE_DATABRICKS ||
        config.value === DatastoreTypeEnum.AZURE_DATABRICKS_PRIVATE_LINK
      ) {
        return azureDatabricksEnabled;
      }
      return true;
    })
    .map(config => ({
      name: config.name,
      icon: config.icon(),
      value: config.value,
    }));

  const handleFormSubmit = (values: FormValues) => {
    if (values.type) {
      onSubmit({
        name: values.name,
        description: values.description,
        type: values.type,
      });
    }
  };

  return (
    <Card className="al-p-8">
      <h3>Type</h3>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleFormSubmit)} className="al-mt-3">
          <div className="al-mb-3">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="example.us-east-1" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="al-mb-3">
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea placeholder="" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="al-mb-3">
            <FormField
              control={form.control}
              name="type"
              render={() => (
                <FormItem>
                  <FormLabel>Select connection type</FormLabel>
                  <Stack gap="md" align="center">
                    {datastoreTypes.map((type, i) => {
                      const isSelected = i === selectedButton;
                      const isConnected = isDatastoreTypeConnected();
                      return (
                        <Tooltip key={i} title={isConnected ? "Already connected" : undefined}>
                          <Button
                            onClick={e =>
                              !isConnected &&
                              handleDatastoreTypeChange(e, i, type.value as EnabledDatastoreType)
                            }
                            className={cn(
                              "al-min-h-[60px] al-w-[150px] al-relative al-py-4 al-px-4 al-h-auto",
                              {
                                "al-border-stroke-brand": isSelected,
                              }
                            )}
                            variant="outline"
                            disabled={isConnected}
                          >
                            {type.icon}
                            <span className="al-whitespace-normal al-text-center al-leading-tight al-break-words">
                              {type.name}
                            </span>
                            {isSelected && (
                              <IconCircleCheckFilled
                                className="al-absolute al-top-1 al-right-1 al-text-[#3f8cff]"
                                size={20}
                              />
                            )}
                          </Button>
                        </Tooltip>
                      );
                    })}
                  </Stack>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <Stack justify={"end"} gap={"xl"}>
            <Button variant={"outline"} asChild>
              <Link to="/settings/datastores">Cancel</Link>
            </Button>
            <div className={"al-ps-4 al-border-l"}>
              <Button type="submit" disabled={!form.watch("name") || !form.watch("type")}>
                Next
                <IconChevronRight size={16} />
              </Button>
            </div>
          </Stack>
        </form>
      </Form>
    </Card>
  );
};

export default DatastoreTypeSelection;
